/**
 * Trip booking step URLs (wyjazdy only).
 * /rezerwacja/1-dane/ · /rezerwacja/2-uczestnicy/ · /rezerwacja/3-podsumowanie/
 * Old slugs stay readable: 1-uczestnicy → participants (step 2), 2-dane → booker (step 1).
 * Query string is kept as-is, except the legacy `step` param which the path replaces.
 */

export const TRAVEL_STEP_SLUGS = {
  1: '1-dane',
  2: '2-uczestnicy',
  3: '3-podsumowanie',
};

const SLUG_TO_STEP = {
  '1-dane': 1,
  '2-uczestnicy': 2,
  '3-podsumowanie': 3,
  '1-uczestnicy': 2,
  '2-dane': 1,
};

export const normalizeBookingPath = (pathname) => {
  const raw = String(pathname || '/').split('?')[0].split('#')[0];
  const withLead = raw.startsWith('/') ? raw : `/${raw}`;
  const collapsed = withLead.replace(/\/{2,}/g, '/');
  if (collapsed === '/') return '/';
  return collapsed.endsWith('/') ? collapsed : `${collapsed}/`;
};

const stripTrailingSlash = (pathname) => {
  const path = normalizeBookingPath(pathname);
  return path === '/' ? '/' : path.replace(/\/$/, '');
};

/**
 * Step encoded in the path, or null when the URL is the bare booking page
 * (that page still opens step 1).
 */
export const readTravelStep = (pathname, bookingPath = '/rezerwacja/') => {
  const path = stripTrailingSlash(pathname);
  const base = stripTrailingSlash(bookingPath || '/rezerwacja/');
  if (path === base) return null;
  if (!path.startsWith(`${base}/`)) return null;
  const slug = path.slice(base.length + 1);
  if (!slug || slug.includes('/')) return null;
  return SLUG_TO_STEP[slug] ?? null;
};

export const buildTravelStepHref = (currentHref, step, bookingPath = '/rezerwacja/') => {
  const url = new URL(currentHref, 'http://hski.test');
  const base = normalizeBookingPath(bookingPath || '/rezerwacja/');
  const n = Math.min(3, Math.max(1, Number(step) || 1));
  const slug = TRAVEL_STEP_SLUGS[n];
  url.pathname = `${base}${slug}/`.replace(/\/{2,}/g, '/');
  url.searchParams.delete('step');
  return `${url.pathname}${url.search}${url.hash}`;
};

/**
 * Direct open of step 2/3 without the data that step needs → step 1.
 * Step 2 needs the booker (contact + orderer). Step 3 also needs participants.
 */
export const resolveTravelEntryStep = (requestedStep, flags = {}) => {
  const step = Math.min(3, Math.max(1, Number(requestedStep) || 1));
  if (step <= 1) return 1;
  if (!flags.contactValid || !flags.ordererValid) return 1;
  if (step >= 3 && !flags.participantsValid) return 1;
  return step;
};

/** In-memory history stand-in used by the unit test and by writeStepToUrl. */
export const pushTravelStep = (history, step, bookingPath, mode = 'push') => {
  const href = history.href || history.location?.href;
  const next = buildTravelStepHref(href, step, bookingPath);
  const state = { bookingStep: Math.min(3, Math.max(1, Number(step) || 1)) };
  if (mode === 'none') return next;
  const current = `${new URL(href, 'http://hski.test').pathname}${new URL(href, 'http://hski.test').search}${new URL(href, 'http://hski.test').hash}`;
  if (mode === 'push' && next !== current) {
    history.pushState(state, '', next);
    return next;
  }
  history.replaceState(state, '', next);
  return next;
};
